import { useState, useMemo, useCallback } from "react";

import { createSortComparator } from "../utils/sort";
import { groupRows } from "../utils/grouping";

import type { DataGridConfig } from "./useDataGridConfig";
import type { SortField, RowOrGroup } from "../types";

export type DataGridState<R extends object> = ReturnType<typeof useDataGridState<R>>;

export function useDataGridState<R extends object>(config: DataGridConfig<R>) {
  // State
  const [columnKeys, setColumnKeys] = useState<readonly string[]>(() => config.columnKeys);
  const [sortFields, setSortFields] = useState(() => config.defaultSorting);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // Computed values
  const columns = useMemo(
    () => columnKeys.filter(key => !config.excludeColumns.includes(key) && Boolean(config.columnMap[key])).map(key => config.columnMap[key]),
    [columnKeys, config.columnMap, config.excludeColumns.join(",")]
  );
  const rows: RowOrGroup<R>[] = useMemo(() => (config.groupColumns.length > 0 ? groupRows(config.data, config.groupColumns) : config.data), [config.data, config.groupColumns]);

  const sortColumns = useMemo(
    () =>
      sortFields
        .filter(({ field }) => !config.excludeColumns.includes(field) && Boolean(config.columnMap[field]))
        .map(({ field, direction }) => ({
          field,
          direction,
          groupValueGetter: config.columnMap[field].groupValueGetter,
          valueGetter: config.columnMap[field].valueGetter,
          type: config.columnMap[field].type,
        })),
    [sortFields, config.columnMap, config.excludeColumns.join(",")]
  );

  const sortedRows = useMemo(() => rows.slice().sort(createSortComparator(sortColumns)), [rows, sortColumns]);

  // Handlers
  const changeSortFields = useCallback((sortFields: SortField[]) => {
    config.onSortChange(sortFields);
    setSortFields(sortFields);
  }, []);

  const changeColumnOrder = useCallback((sourceKey: string, targetKey: string) => {
    setColumnKeys(columnKeys => {
      const sourceColumnIndex = columnKeys.findIndex(key => key === sourceKey);
      const targetColumnIndex = columnKeys.findIndex(key => key === targetKey);

      const newColumnKeys = columnKeys.slice();
      newColumnKeys.splice(sourceColumnIndex, 1);
      newColumnKeys.splice(targetColumnIndex, 0, sourceKey);

      return newColumnKeys;
    });
  }, []);

  const toggleRowGroup = useCallback((key: string) => {
    setExpandedRows(state => ({ ...state, [key]: !state[key] }));
  }, []);

  return { columns, sortFields, rows: sortedRows, expandedRows, toggleRowGroup, changeSortFields, changeColumnOrder };
}
